// T2T-05-C4 — Per-request SSE subscription with exponential backoff reconnect
// and missed-message catch-up via after_id.

import { useEffect, useRef, useState } from "react";
import { buildChatStreamUrl, fetchMessages } from "../../../../../api/t2tMessagesApi";

const RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000];
const MAX_ATTEMPTS = RETRY_DELAYS.length;

/**
 * @param {string|number} requestId
 * @param {{
 *   onMessageNew:     (msg) => void,
 *   onMessageEdited:  (evt) => void,
 *   onMessageDeleted: (evt) => void,
 *   onParticipantRead:(evt) => void,
 *   onUserTyping:     (evt) => void,
 *   onUserStoppedTyping:(evt) => void,
 *   getLastMessageId:    () => number|null,
 * }} handlers
 */
export default function useChatSse(requestId, handlers) {
  // 'connecting' | 'connected' | 'disconnected' | 'failed'
  const [status, setStatus] = useState("connecting");
  const esRef       = useRef(null);
  const attemptsRef = useRef(0);
  const closedRef   = useRef(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!requestId) return undefined;
    closedRef.current = false;

    const open = () => {
      if (closedRef.current) return;
      const url = buildChatStreamUrl(requestId);
      const es = new EventSource(url);
      esRef.current = es;
      setStatus("connecting");

      es.addEventListener("stream.open", () => {
        setStatus("connected");
        attemptsRef.current = 0;
        // Catch missed messages since the last one we have on screen
        const lastId = handlersRef.current.getLastMessageId?.();
        if (lastId) {
          fetchMessages(requestId, { limit: 100, after_id: lastId })
            .then((resp) => {
              const missed = resp?.data?.data?.messages || [];
              for (const m of missed) handlersRef.current.onMessageNew?.(m);
            })
            .catch(() => {});
        }
      });

      const safeJSON = (raw) => { try { return JSON.parse(raw); } catch { return null; } };

      es.addEventListener("message.new",       (e) => { const d = safeJSON(e.data); if (d) handlersRef.current.onMessageNew?.(d); });
      es.addEventListener("message.edited",    (e) => { const d = safeJSON(e.data); if (d) handlersRef.current.onMessageEdited?.(d); });
      es.addEventListener("message.deleted",   (e) => { const d = safeJSON(e.data); if (d) handlersRef.current.onMessageDeleted?.(d); });
      es.addEventListener("participant.read",  (e) => { const d = safeJSON(e.data); if (d) handlersRef.current.onParticipantRead?.(d); });
      es.addEventListener("user.typing",       (e) => { const d = safeJSON(e.data); if (d) handlersRef.current.onUserTyping?.(d); });
      es.addEventListener("user.stopped_typing", (e) => { const d = safeJSON(e.data); if (d) handlersRef.current.onUserStoppedTyping?.(d); });

      es.onerror = () => {
        if (closedRef.current) return;
        try { es.close(); } catch (_) { /* gone */ }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        const delay = RETRY_DELAYS[attemptsRef.current];
        attemptsRef.current += 1;
        setStatus("disconnected");
        setTimeout(open, delay);
      };
    };

    open();

    return () => {
      closedRef.current = true;
      if (esRef.current) {
        try { esRef.current.close(); } catch (_) { /* noop */ }
        esRef.current = null;
      }
    };
  }, [requestId]);

  const reconnectNow = () => {
    if (esRef.current) {
      try { esRef.current.close(); } catch (_) { /* noop */ }
    }
    attemptsRef.current = 0;
    closedRef.current = false;
    setStatus("connecting");
    // re-open by toggling — simplest is to force a remount via the hook owner;
    // here we just request the consumer to call us again via key change.
  };

  return { status, reconnectNow };
}
